/* eslint-disable max-len */
import React, {
	useEffect,
	useRef,
	useState,
} from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import Draggable from 'react-draggable';
import styled from 'styled-components';
import Socket from '../../../connection/socket';
import SlidingMenu, {
	SlidingMenuOption,
} from '../../general-components/sliding-menu';
import DebugVideo from './video/debug-video';
import DebugScheduledStream from './broadcasting/debug-scheduled-stream';
import AVUI from '../av-ui';
import { setShowDeprecationWarning } from '../../../stores/actions/user-interface';
import DebugBroadcast from './broadcasting/debug-broadcast';
import Icon, { ICONS } from '../../general-components/icon';
import DebugFlags from './debug-flags';
import DebugToggles from './debug-toggles';
import DebugExport from './debug-export';
import DebugSettings from './debug-settings';
import AuthorizeDevice from '../../device-auth/authorize-device';
import DebugSimulcast from './broadcasting/debug-simulcast';
import DebugSrtStreams from './video/debug-srt-streams';
import DebugMasks from './profiles/debug-masks';
import DebugOnsiteLocations from './video/debug-onsite-locations';
import DebugTitles from './titles/debug-titles';
import DebugAlerts from './alerts/debug-alerts';
import DebugAddStylePack from './titles/debug-add-style-pack';
import DebugCreateThemeMap from './titles/debug-create-theme-map';
import DebugSegments from './debug-segments';
import DebugColorTokens from './debug-color-tokens';
import DebugPstn from './pstn/debug-pstn';
import DebugIngresses from './video/debug-ingresses';

export enum DebugLocalStorage {
	PanelEnabled = 'debug-panel',
	StalePanelEnabled = '_debug-panel-enabled',
	OverlaysOn = '_debug-panel-overlays-on',
	EchoCancellationOn = '_debug-panel-echo-cancellation-on',
	SocketCanDispatch = '_debug-panel-socket-can-dispatch',
	DebugOpentok = '_debug-opentok',
	debugKillServer = '_debug-kill-server',
	debugDisconnectProducer = '_debug-disconnect-producer',
	debugLogIsoAlerts = '_debug-log-iso-alerts',
	avUI = '_debug-AV-UI',
}

interface DebugPanelProps {
	socket: Socket | null;
}

const DEBUG_TABS = {
	FLAGS: 0,
	TOGGLES: 1,
	VIDEO: 3,
	SETTINGS: 5,
	EXPORT: 6,
	BROADCAST: 10,
	LIMITED_INPUT_AUTH: 11,
	SRT_STREAMS: 12,
	FEEDS: 13,
	TITLES: 14,
	STYLE_PACKS: 15,
	ALERTS: 16,
	PROFILES: 17,
	CREATE_THEME_MAP: 18,
	SEGMENTS: 19,
	COLOR_TOKENS: 20,
	INGRESSES: 21,
	PHONE: 22,
};

const StyledHr = styled.hr`
	border: 1px solid #3C3A42;
`;

const tabOptions1 = [
	{ label: 'Feature Flags', data: DEBUG_TABS.FLAGS, icon: ICONS.SPARKLE },
	{ label: 'Toggles', data: DEBUG_TABS.TOGGLES, icon: ICONS.SETTINGS_SLIDERS },
	{ label: 'Video', data: DEBUG_TABS.VIDEO, icon: ICONS.VIDEO },
	{ label: 'Broadcast', data: DEBUG_TABS.BROADCAST, icon: ICONS.STREAM_PLAY },
	{ label: 'Colors', data: DEBUG_TABS.COLOR_TOKENS, icon: ICONS.EYEDROPPER },
	{ label: 'Room', data: DEBUG_TABS.SETTINGS, icon: ICONS.SETTINGS },
	{ label: 'Import/Export', data: DEBUG_TABS.EXPORT, icon: ICONS.DOWNLOAD_ICON },
	{ label: 'Limited Input Auth', data: DEBUG_TABS.LIMITED_INPUT_AUTH, icon: ICONS.LOCK },
];
const tabOptions2 = [
	{ label: 'SRT Streams', data: DEBUG_TABS.SRT_STREAMS, icon: ICONS.PERSON_BLUR },
	{ label: 'Profile Masks', data: DEBUG_TABS.PROFILES, icon: ICONS.PRESENTERS_UNFILLED },
	{ label: 'Feeds', data: DEBUG_TABS.FEEDS, icon: ICONS.VIRTUAL_BACKGROUND },
	{ label: 'Titles', data: DEBUG_TABS.TITLES, icon: ICONS.TITLES },
	{ label: 'Add style packs', data: DEBUG_TABS.STYLE_PACKS, icon: ICONS.MEDIA },
	{ label: 'Create theme map', data: DEBUG_TABS.CREATE_THEME_MAP, icon: ICONS.DESIGN },
	{ label: 'Alerts', data: DEBUG_TABS.ALERTS, icon: ICONS.ERROR },
	{ label: 'Segments', data: DEBUG_TABS.SEGMENTS, icon: ICONS.TELEPROMPTER },
	{ label: 'Ingresses', data: DEBUG_TABS.INGRESSES, icon: ICONS.VIDEO },
	{ label: 'Phone', data: DEBUG_TABS.PHONE, icon: ICONS.PHONE },
];
const allTabOptions = [...tabOptions1, ...tabOptions2];

export default function DebugPanel({ socket }: DebugPanelProps) {
	const dispatch = useDispatch();
	const draggableRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		dispatch(setShowDeprecationWarning(false));
	}, []);

	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [isExpandedHorizontally, setIsExpandedHorizontally] = useState<boolean>(false);
	const [tab, setTab] = useState<number>(DEBUG_TABS.FLAGS);

	const setDebugPanelOpen = (isOpen: boolean, doExpand: boolean) => {
		setIsExpanded(isOpen && doExpand);
		localStorage.setItem(DebugLocalStorage.PanelEnabled, isOpen ? 'true' : 'false');
	};

	useEffect(() => {
		// Load Settings
		const panelOpen = localStorage.getItem(DebugLocalStorage.PanelEnabled) === 'true';
		setDebugPanelOpen(panelOpen ? true : false, false);
	}, []);

	return (<>
		<Draggable nodeRef={draggableRef} handle='.handle'>
			<div
			ref={draggableRef}
			className={classNames(
				'debug-panel-container',
				{ expanded: isExpanded, 'expanded-horizontally': (isExpandedHorizontally && isExpanded) },
			)}
		>
				<div className='debug-panel-header-controls'>
					<div className={classNames('debug-expander handle')}>
						<h6 className='bl-font-header text-dark padding-left-small'>Debug Panel</h6>
						<Icon
							size={18}
							color='black'
							name={ICONS.DRAG}
						/>
					</div>
					<div
						onClick={() => setDebugPanelOpen(!isExpanded, !isExpanded)}
						className={classNames('debug-expander vertical')}
					>
						<Icon
							size={18}
							color='black'
							name={isExpanded ? ICONS.KEYBOARD_ARROW_UP : ICONS.KEYBOARD_ARROW_DOWN}
						/>
					</div>
					<div
						onClick={() => setIsExpandedHorizontally((prev: boolean) => !prev)}
						className={classNames('debug-expander horizontal', { 'do-hide': !isExpanded })}
					>
						<Icon
							size={18}
							color='black'
							name={isExpandedHorizontally ? ICONS.KEYBOARD_ARROW_LEFT : ICONS.KEYBOARD_ARROW_RIGHT}
						/>
					</div>
				</div>
				{isExpanded && <div className="debug-panel">
					<SlidingMenu
						options={isExpandedHorizontally ? allTabOptions : tabOptions1}
						selectedOption={(isExpandedHorizontally ? allTabOptions : tabOptions1).find(
							(op: SlidingMenuOption) => op.data === tab,
						) ?? null}
						callback={(option: SlidingMenuOption) => { setTab(option.data); }}
						className='position-relative width-calc-full'
						fullWidthDivider={true}
					/>
					{isExpandedHorizontally ? null : <SlidingMenu
						options={tabOptions2}
						selectedOption={tabOptions2.find(
							(op: SlidingMenuOption) => op.data === tab,
						) ?? null}
						callback={(option: SlidingMenuOption) => { setTab(option.data); }}
						className='position-relative width-calc-full'
						fullWidthDivider={true}
					/>}
					<div className={classNames('debug-panel-below-menu', tab === DEBUG_TABS.FEEDS && 'srt')}>
						{tab === DEBUG_TABS.FLAGS && <DebugFlags/>}
						{tab === DEBUG_TABS.TOGGLES && <DebugToggles/>}

						{tab === DEBUG_TABS.SETTINGS && <DebugSettings socket={socket}/>}

						{tab === DEBUG_TABS.EXPORT && <DebugExport/>}
						{tab === DEBUG_TABS.VIDEO && (
							<DebugVideo/>
						)}

						{tab === DEBUG_TABS.BROADCAST && (<>
							<DebugSimulcast />
							<StyledHr />
							<DebugBroadcast />
							<StyledHr />
							<DebugScheduledStream />
						</>)}
						{tab === DEBUG_TABS.LIMITED_INPUT_AUTH && (
							<AuthorizeDevice/>
						)}
						{tab === DEBUG_TABS.TITLES && (
							<DebugTitles/>
						)}
						{tab === DEBUG_TABS.SRT_STREAMS && <DebugSrtStreams />}
						{tab === DEBUG_TABS.PROFILES && <DebugMasks canExpand={isExpandedHorizontally}/>}
						{tab === DEBUG_TABS.FEEDS && (
							<>
								<DebugSrtStreams />
								<StyledHr />
								<DebugOnsiteLocations />
								<StyledHr />
								<DebugIngresses />
							</>
						)}
						{tab === DEBUG_TABS.INGRESSES && <DebugIngresses />}
						{tab === DEBUG_TABS.ALERTS && (
							<DebugAlerts/>
						)}
						 {tab === DEBUG_TABS.STYLE_PACKS && <DebugAddStylePack/>}
						 {tab === DEBUG_TABS.CREATE_THEME_MAP && <DebugCreateThemeMap/>}
						 {tab === DEBUG_TABS.SEGMENTS && <DebugSegments/>}
						 {tab === DEBUG_TABS.COLOR_TOKENS && <DebugColorTokens/>}
						 {tab === DEBUG_TABS.PHONE && <DebugPstn/>}
					</div>
				</div>}
			</div>
		</Draggable>
		<AVUI />
	</>);
}
